import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { PlanoAcao } from '@/types/plano-acao';
import { toast } from 'sonner';

export function usePlanoAcao(respostaId?: string, campoId?: string) {
  const queryClient = useQueryClient();
  
  // Buscar planos de ação de uma resposta
  const { data: planosAcao, isLoading } = useQuery({
    queryKey: ['planos-acao', respostaId, campoId],
    queryFn: async () => {
      let query = supabase
        .from('formulario_planos_acao')
        .select(`
          *,
          categoria:categorias_servico!categoria_id(id, nome),
          criador:users!criado_por(id, name),
          responsavel:users!responsavel_id(id, name)
        `)
        .order('criado_em', { ascending: false });
      
      if (respostaId) query = query.eq('resposta_id', respostaId);
      if (campoId) query = query.eq('campo_id', campoId);
      
      const { data, error } = await query;
      if (error) throw error;
      return data as PlanoAcao[];
    },
    enabled: !!(respostaId || campoId),
  });
  
  // Criar plano de ação
  const createPlanoAcao = useMutation({
    mutationFn: async (planoData: Partial<PlanoAcao>) => {
      const { data, error } = await supabase
        .from('formulario_planos_acao')
        .insert(planoData as any)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['planos-acao'] });
      toast.success('Plano de ação criado com sucesso!');
    },
    onError: (error: any) => {
      toast.error('Erro ao criar plano de ação: ' + error.message);
    },
  });
  
  // Atualizar plano de ação
  const updatePlanoAcao = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<PlanoAcao> }) => {
      const { data, error } = await supabase
        .from('formulario_planos_acao')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['planos-acao'] });
      toast.success('Plano de ação atualizado!');
    },
  });
  
  return {
    planosAcao,
    isLoading,
    createPlanoAcao: createPlanoAcao.mutate,
    updatePlanoAcao: updatePlanoAcao.mutate,
    isCreating: createPlanoAcao.isPending,
    isUpdating: updatePlanoAcao.isPending,
  };
}
